import { FC } from 'react'
import { Box, Card, CardContent, Chip, Typography } from '@mui/material'
import { format, isValid, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { HCPFlowEnum } from '../../../Services/HCP/flows/hcp-flow.interface'
import { IHCPFlowKanbanCard } from '../../../Services/HCP/flows/hcp-flow-card.interface'
import { isItemArtApproved, isOrderWithPendingArts } from '../../../Services/HCP/flows/hcp-flow-card.helpers'
import { getLatestFlowHistoryActor } from '../../../Services/HCP/flows/hcp-flow-history.helpers'

interface IFlowManagementCardProps {
  card: IHCPFlowKanbanCard
  flow?: HCPFlowEnum
  isMoving?: boolean
  onClick?: (card: IHCPFlowKanbanCard) => void
}

function formatShortDate(deadline?: string | null): string | null {
  if (!deadline) return null
  const date = parseISO(deadline)
  if (!isValid(date)) return null
  return format(date, 'dd/MM/yy HH:mm', { locale: ptBR })
}

function formatQuantity(value?: number | null): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null
  return value.toLocaleString('pt-BR')
}

const FlowManagementCard: FC<IFlowManagementCardProps> = ({ card, flow, isMoving, onClick }) => {
  const isSheetFlow = flow === HCPFlowEnum.SHEET
  const isLogisticsFlow = flow === HCPFlowEnum.LOGISTICS
  const isProductionFlow = flow === HCPFlowEnum.PRODUCTION
  const isOperationalFlow = isLogisticsFlow || isProductionFlow
  const isLogisticsOrderCard = Boolean(card.isLogisticsOrderCard)
  const showArtChips = flow === HCPFlowEnum.AWAITING_ART || isSheetFlow
  const itemArtApproved = isItemArtApproved(card, flow)
  const orderWithPendingArts = isOrderWithPendingArts(card)
  const logisticsItems = card.logisticsOrderItems ?? []

  const orderLabel = card.order?.nsOrderId
    ? `Pedido ${card.order.nsOrderId}`
    : isLogisticsOrderCard
      ? 'Pedido'
      : `Item ${card.id}`

  const customerName = card.order?.customer?.name ?? null
  const productName = card.product?.name ?? null
  const productOrderCode = card.productOrderCode ?? null
  const displayCode = card.displayCode ?? card.hcpCurrentFlowDisplayCode
  const quantityLabel = formatQuantity(card.totalQuantity)
  const volumetryLabel = card.volumetry ?? null
  const dlCliente = formatShortDate(card.customerDeadline)
  const dlFluxo = formatShortDate(card.hcpCurrentFlowDeadline)
  const lastMovedBy = isOperationalFlow ? getLatestFlowHistoryActor(card.hcpFlowHistories) : null

  return (
    <Card
      elevation={1}
      onClick={() => !isMoving && onClick?.(card)}
      sx={{
        flexShrink: 0,
        borderRadius: 2,
        cursor: onClick && !isMoving ? 'pointer' : 'default',
        bgcolor: '#fff',
        position: 'relative',
        '&:hover': { boxShadow: onClick && !isMoving ? 4 : 3 },
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="subtitle2" fontWeight={700} color="text.primary" noWrap title={orderLabel}>
          {orderLabel}
        </Typography>

        {customerName && (
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ mt: 0.75 }}
            noWrap
            title={customerName}
          >
            {customerName}
          </Typography>
        )}

        {isLogisticsOrderCard && (
          <Chip
            label={`${logisticsItems.length} itens prontos`}
            size="small"
            sx={{
              mt: 0.75,
              height: 20,
              fontSize: '0.68rem',
              fontWeight: 700,
              bgcolor: 'rgba(46,125,50,0.12)',
              color: '#2e7d32',
            }}
          />
        )}

        {isLogisticsOrderCard && logisticsItems.length > 0 && (
          <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {logisticsItems.slice(0, 3).map((item) => (
              <Box
                key={String(item.id)}
                sx={{
                  px: 0.75,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: 'rgba(0,0,0,0.03)',
                }}
              >
                <Typography variant="caption" color="text.primary" display="block" noWrap title={item.productName ?? undefined}>
                  {item.productOrderCode ? `${item.productOrderCode} · ` : ''}{item.productName ?? 'Item'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {[
                    item.volumetry,
                    item.totalQuantity != null ? `Qtd ${formatQuantity(item.totalQuantity)}` : null,
                  ].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            ))}
            {logisticsItems.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                +{logisticsItems.length - 3} itens
              </Typography>
            )}
          </Box>
        )}

        {!isLogisticsOrderCard && productName && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.5, lineHeight: 1.4 }}
            title={productName}
          >
            {productName}
          </Typography>
        )}

        {!isLogisticsOrderCard && productOrderCode && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Cod. produto: <strong>{productOrderCode}</strong>
          </Typography>
        )}

        {isOperationalFlow && card.serviceOrderId && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            OS: <strong>{card.serviceOrderId}</strong>
          </Typography>
        )}

        {!isLogisticsOrderCard && (volumetryLabel || quantityLabel) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            {volumetryLabel && (
              <Chip
                label={volumetryLabel}
                size="small"
                sx={{ height: 20, fontSize: '0.68rem', bgcolor: '#eceff3', color: 'text.secondary' }}
              />
            )}
            {quantityLabel && (
              <Chip
                label={`Qtd: ${quantityLabel}`}
                size="small"
                sx={{ height: 20, fontSize: '0.68rem', bgcolor: '#eceff3', color: 'text.secondary' }}
              />
            )}
          </Box>
        )}

        {(dlFluxo || dlCliente) && (
          <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {dlFluxo && (
              <Typography variant="caption" color="text.secondary" display="block">
                DL Fluxo: {dlFluxo}
              </Typography>
            )}
            {dlCliente && (
              <Typography variant="caption" color="text.secondary" display="block">
                DL Cliente: {dlCliente}
              </Typography>
            )}
          </Box>
        )}

        {isLogisticsOrderCard && quantityLabel && (
          <Box sx={{ mt: 0.75 }}>
            <Chip
              label={`Total: ${quantityLabel} un`}
              size="small"
              sx={{ height: 20, fontSize: '0.68rem', bgcolor: '#eceff3', color: 'text.secondary' }}
            />
          </Box>
        )}

        {displayCode && (
          <Box sx={{ mt: 0.75 }}>
            <Chip
              label={`Status: ${displayCode}`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.68rem',
                fontWeight: 600,
                bgcolor: 'rgba(21,101,192,0.10)',
                color: '#1565c0',
              }}
            />
          </Box>
        )}

        {!itemArtApproved && showArtChips && (
          <Box sx={{ mt: 0.75 }}>
            <Chip label="Arte pendente" size="small" color="warning" variant="outlined" />
          </Box>
        )}

        {itemArtApproved && showArtChips && (
          <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Chip label="Arte aprovada" size="small" color="success" variant="outlined" />
            {isSheetFlow && orderWithPendingArts && (
              <Chip
                label="Outros itens do pedido pendentes"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontSize: '0.65rem' }}
              />
            )}
          </Box>
        )}

        {lastMovedBy && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.75, fontSize: '0.65rem' }}
            noWrap
            title={`Última movimentação por ${lastMovedBy}`}
          >
            Movido por {lastMovedBy}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

export default FlowManagementCard
